import { useState, useRef, useEffect } from 'react';
import React from 'react';
import { DataRoomFile } from '@/types/documents/documents.types';
import { useDocuments } from '@/hooks/documents/useDocuments';
import { UseAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@tw/tw-component-library';
import { Modal, Button, Input } from '@/ui';
import { getFileIcon } from '@/utils/documents/documentHelpers';
import { uploadLocalDocuments } from '@/apis/data/document/post/uploadLocalDocuments';
import { linkFileToAsk } from '@/apis/data/dataRoom/post/linkFileToAsk';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';
import { useSignalRHub } from '@/hooks/useSignalRHub';
import { useWorkspaceRelayHubHandlers } from '@/hooks/signalR/useWorkspaceRelayHubHandlers';
import { WorkspaceRelayHubEvents } from '@/handlers/signalR/workspaceRelayHub';
import { UploadProcessingIndicator } from '@/components/common/Ask/UploadProcessingIndicator';
import styled from 'styled-components';
import { COLORS } from '../Activities/ActivityMenu/styles/constants';

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 60vh;
  overflow-y: auto;
  padding: 1rem 0;
`;

const DocumentItem = styled.div<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid
    ${({ selected, theme }) =>
      selected ? theme.colors.primary : COLORS.BORDER_LIGHT};
  border-radius: 8px;
  cursor: pointer;
  background-color: ${({ selected }) =>
    selected ? COLORS.BACKGROUND : COLORS.BACKGROUND};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ selected }) =>
      selected ? COLORS.BACKGROUND : COLORS.BACKGROUND};
  }
`;

const DocumentIcon = styled.div`
  font-size: 2rem;
  flex-shrink: 0;
`;

const DocumentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocumentName = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DocumentMeta = styled.div`
  font-size: 0.75rem;
  color: ${COLORS.TEXT_SECONDARY};
  display: flex;
  gap: 1rem;
`;

const SearchContainer = styled.div`
  margin-bottom: 1rem;
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${COLORS.TEXT_SECONDARY};
  font-style: italic;
`;

const SelectedBadge = styled.div`
  background-color: ${COLORS.PRIMARY};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const LinkedBadge = styled.div`
  background-color: ${COLORS.BACKGROUND};
  color: ${COLORS.PRIMARY};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.5rem;
`;

interface DocumentSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string | undefined;
  selectedDocumentIds: string[];
  linkedDocumentIds?: string[];
  askSessionId?: string;
  uploadingFileNames?: string[];
  onSelectDocuments: (documents: DataRoomFile[]) => void;
  onUploadStart?: (fileNames: string[]) => void;
  onDocumentsUploaded?: () => void;
}

export const DocumentSelector: React.FC<DocumentSelectorProps> = ({
  isOpen,
  onClose,
  workspaceId,
  selectedDocumentIds,
  linkedDocumentIds = [],
  askSessionId,
  uploadingFileNames = [],
  onSelectDocuments,
  onUploadStart,
  onDocumentsUploaded,
}) => {
  const LINKED_STATUS_TUTORIAL_TITLE = 'Linked status';

  const { getAccessToken } = UseAuth();
  const { handleError, handleSuccess } = useWorkspaceErrorHandler(workspaceId);
  const [searchQuery, setSearchQuery] = useState('');
  const [localSelectedIds, setLocalSelectedIds] =
    useState<string[]>(selectedDocumentIds);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [versionIfExists, setVersionIfExists] = useState(true);
  const [uploadedFileNames, setUploadedFileNames] = useState<string[]>([]);

  const normalizeUploadFileName = (name: string | undefined | null): string => {
    if (!name) return '';
    const lower = name.toLowerCase().trim();
    return lower.replace(/\.[^./\\]+$/, '');
  };

  // Use ref to track uploaded file names for SignalR handler
  const uploadedFileNamesRef = useRef<string[]>([]);

  // Sync ref with state
  useEffect(() => {
    uploadedFileNamesRef.current = uploadedFileNames;
  }, [uploadedFileNames]);

  const {
    filteredDocuments,
    isLoading,
    getFileCollectionInfo,
    getWorkspaceData,
    documentsWithCollection,
  } = useDocuments({
    workspaceId,
    getAccessToken,
  });

  // Store a ref to track documents before upload for comparison
  const documentsBeforeUploadRef =
    React.useRef<DataRoomFile[]>(filteredDocuments);

  React.useEffect(() => {
    documentsBeforeUploadRef.current = filteredDocuments;
  }, [filteredDocuments]);

  // Set up SignalR for workspace messages
  useSignalRHub({ hubName: 'workspaceRelayHub' });

  // Handle SignalR events for file uploads
  useWorkspaceRelayHubHandlers({
    workspaceId,
    onDataRefresh: async () => {
      await getWorkspaceData();
    },
    enableToasts: false, // We'll handle toasts ourselves
    enableLogging: true,
    onEventReceived: async (event, _data, parsed) => {
      if (event === WorkspaceRelayHubEvents.DATAROOMFILE_ADDED_TO_WORKSPACE) {
        // File has been added to workspace, check if it's one we uploaded
        const currentUploadedFileNames = uploadedFileNamesRef.current;
        const fileNameCandidate = (parsed as { fileName?: unknown } | null)
          ?.fileName;
        if (
          currentUploadedFileNames.length > 0 &&
          typeof fileNameCandidate === 'string'
        ) {
          const fileName = fileNameCandidate;
          const normalizedEventFileName = normalizeUploadFileName(fileName);
          const isOurFile = currentUploadedFileNames.some(
            (uploadedName) =>
              normalizeUploadFileName(uploadedName) === normalizedEventFileName,
          );

          if (isOurFile) {
            // Refresh workspace data to get the new file
            await getWorkspaceData();

            // Wait a moment for the file to be fully available
            setTimeout(async () => {
              await getWorkspaceData();

              // Get updated documents list
              const updatedDocuments =
                documentsWithCollection && documentsWithCollection.length > 0
                  ? documentsWithCollection
                  : filteredDocuments;

              // Find the uploaded file by name
              const uploadedDoc = updatedDocuments.find(
                (doc) =>
                  normalizeUploadFileName(doc.name) === normalizedEventFileName,
              );

              if (uploadedDoc && askSessionId) {
                // Link the file to the ask session
                try {
                  await linkFileToAsk({
                    getAccessToken,
                    workspaceId: workspaceId || '',
                    body: {
                      dataRoomFileId: uploadedDoc.dataRoomFileId,
                      instanceId: askSessionId,
                    },
                  });

                  // Remove this file from the tracking list and check if all are done
                  setUploadedFileNames((prev) => {
                    const updated = prev.filter(
                      (name) =>
                        normalizeUploadFileName(name) !==
                        normalizedEventFileName,
                    );
                    uploadedFileNamesRef.current = updated;

                    // Keep parent indicator in sync as each file completes.
                    if (onUploadStart) {
                      onUploadStart(updated);
                    }

                    // Check if all files have been processed
                    if (updated.length === 0) {
                      // All files processed - use setTimeout to ensure state updates complete
                      setTimeout(() => {
                        setIsUploading(false);
                        handleSuccess(
                          'Documents uploaded and linked to ask session successfully',
                        );
                        // Notify parent component to refresh session data and clear uploading status
                        if (onDocumentsUploaded) {
                          onDocumentsUploaded();
                        }
                      }, 100);
                    }

                    return updated;
                  });
                  // File linking is already handled; no additional tracking required here.
                } catch (err) {
                  console.error(
                    `Failed to link file ${uploadedDoc.dataRoomFileId} to ask session:`,
                    err,
                  );
                  handleError(
                    err instanceof Error
                      ? err
                      : new Error('Failed to link file to ask session'),
                    'Failed to link file to ask session',
                  );

                  // Remove from tracking even on error so we don't get stuck
                  setUploadedFileNames((prev) => {
                    const updated = prev.filter(
                      (name) =>
                        normalizeUploadFileName(name) !==
                        normalizedEventFileName,
                    );
                    uploadedFileNamesRef.current = updated;

                    if (onUploadStart) {
                      onUploadStart(updated);
                    }

                    if (updated.length === 0) {
                      setIsUploading(false);
                    }
                    return updated;
                  });
                }
              }
            }, 500); // Short delay for file to be available
          }
        }
      } else if (
        event === WorkspaceRelayHubEvents.FILE_UPLOAD_ERRORED ||
        event === WorkspaceRelayHubEvents.URL_UPLOAD_ERRORED ||
        event === WorkspaceRelayHubEvents.CENTRAL_UPLOAD_ERRORED
      ) {
        // Upload error occurred
        setIsUploading(false);
        handleError(
          new Error(
            typeof (parsed as { message?: unknown } | null)?.message ===
              'string'
              ? ((parsed as { message?: unknown } | null)?.message as string)
              : 'File upload failed',
          ),
          'File upload failed',
        );
        setShowUploadModal(false);
        setPendingFiles([]);
        setUploadedFileNames([]);
        uploadedFileNamesRef.current = [];
      }
    },
  });

  // Filter documents by search query
  const filteredBySearch = filteredDocuments.filter((doc) =>
    doc.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleToggleDocument = (doc: DataRoomFile) => {
    setLocalSelectedIds((prev) => {
      if (prev.includes(doc.dataRoomFileId)) {
        return prev.filter((id) => id !== doc.dataRoomFileId);
      } else {
        return [...prev, doc.dataRoomFileId];
      }
    });
  };

  const handleConfirm = () => {
    const selectedDocs = filteredDocuments.filter((doc) =>
      localSelectedIds.includes(doc.dataRoomFileId),
    );
    onSelectDocuments(selectedDocs);
    onClose();
  };

  const handleCancel = () => {
    setLocalSelectedIds(selectedDocumentIds);
    onClose();
  };

  const handleFileSelectWrapper = (files: File[]) => {
    setPendingFiles(files);
  };

  const handleUploadDocuments = async () => {
    if (!workspaceId || !askSessionId || pendingFiles.length === 0) {
      handleError(
        new Error('Missing required information for upload'),
        'Cannot upload documents',
      );
      return;
    }

    try {
      setIsUploading(true);

      // Track which files we're uploading (by name)
      const fileNames = pendingFiles.map((f) => f.name);
      setUploadedFileNames(fileNames);
      uploadedFileNamesRef.current = fileNames;

      // Upload files
      const uploadResponse = await uploadLocalDocuments({
        getAccessToken,
        workspaceId,
        files: pendingFiles,
        versionIfExists,
        askSessionId,
      });

      if (uploadResponse && (uploadResponse.success || uploadResponse.data)) {
        // Files uploaded successfully (200 response)
        // Close modal immediately - SignalR will handle the rest
        setShowUploadModal(false);
        setPendingFiles([]);

        // Notify parent about upload start
        if (onUploadStart) {
          onUploadStart(fileNames);
        }

        // SignalR event will handle the rest (linking files when they're added)
        // We just need to wait for DATAROOMFILE_ADDED_TO_WORKSPACE events
        // Don't set isUploading to false here - wait for SignalR events
      } else {
        // Upload failed immediately
        setIsUploading(false);
        setShowUploadModal(false);
        setPendingFiles([]);
        setUploadedFileNames([]);
        uploadedFileNamesRef.current = [];
        handleError(new Error('Upload failed'), 'Failed to upload documents');
      }
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to upload documents');
      handleError(error, 'Failed to upload documents');
      setIsUploading(false);
      setShowUploadModal(false);
      setPendingFiles([]);
      setUploadedFileNames([]);
      uploadedFileNamesRef.current = [];
    }
  };

  const resetUploadModal = () => {
    if (!isUploading) {
      setShowUploadModal(false);
      setPendingFiles([]);
      setVersionIfExists(true);
      setUploadedFileNames([]);
      uploadedFileNamesRef.current = [];
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    let hasAutoAdvancedLinkedStep = false;

    const tryAdvanceLinkedStatusTutorialStep = () => {
      if (hasAutoAdvancedLinkedStep) return;

      const hasLinkedBadge = Boolean(
        document.querySelector("[data-tour='doc-selector-linked-badge']"),
      );
      if (!hasLinkedBadge) return;

      const bodyText = document.body.textContent || '';
      const isOnLinkedStatusStep = bodyText.includes(
        LINKED_STATUS_TUTORIAL_TITLE,
      );
      if (!isOnLinkedStatusStep) return;

      const nextButton = Array.from(document.querySelectorAll('button')).find(
        (button) => {
          if (button.disabled) return false;
          const label = (button.textContent || '').trim().toLowerCase();
          return (
            label === 'next' ||
            label === 'continue' ||
            label === 'finish' ||
            label === 'done' ||
            label === 'complete'
          );
        },
      );

      if (!nextButton) return;
      nextButton.click();
      hasAutoAdvancedLinkedStep = true;
    };

    // Check immediately and then watch for delayed backend/SignalR updates.
    tryAdvanceLinkedStatusTutorialStep();
    const observer = new MutationObserver(() => {
      tryAdvanceLinkedStatusTutorialStep();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [isOpen, linkedDocumentIds]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Select Documents"
      size="large"
    >
      <div data-tour="doc-selector-select-documents-modal">
        <SearchContainer>
          <Input
            type="text"
            label="Search documents"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Type to search..."
            data-tour="doc-selector-search-input"
          />
        </SearchContainer>

        <UploadProcessingIndicator
          uploadingFileNames={uploadingFileNames}
          dataTour="doc-selector-uploading-indicator"
        />
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {filteredBySearch.length === 0 ? (
              <EmptyState>
                {searchQuery
                  ? 'No documents match your search'
                  : 'No documents available'}
              </EmptyState>
            ) : (
              <DocumentList>
                {filteredBySearch.map((doc) => {
                  const isSelected = localSelectedIds.includes(
                    doc.dataRoomFileId,
                  );
                  const isLinked = linkedDocumentIds.includes(
                    doc.dataRoomFileId,
                  );
                  const collectionInfo = getFileCollectionInfo(
                    doc.dataRoomFileId,
                  );

                  return (
                    <DocumentItem
                      key={doc.dataRoomFileId}
                      selected={isSelected}
                      onClick={() => handleToggleDocument(doc)}
                      data-tour="doc-selector-document-item"
                      data-document-name={doc.name || ''}
                    >
                      <DocumentIcon>
                        {getFileIcon(doc.name, doc.mimeType)}
                      </DocumentIcon>
                      <DocumentInfo>
                        <DocumentName>
                          {doc.name || 'Untitled Document'}
                          {isLinked && (
                            <LinkedBadge data-tour="doc-selector-linked-badge">
                              💬 Linked
                            </LinkedBadge>
                          )}
                        </DocumentName>
                        <DocumentMeta>
                          {doc.readableSize && <span>{doc.readableSize}</span>}
                          {doc.mimeType && (
                            <span>
                              {doc.mimeType.split('/')[1]?.toUpperCase()}
                            </span>
                          )}
                          {collectionInfo && (
                            <span>
                              {collectionInfo.isSmart ? '✨' : '📁'}{' '}
                              {collectionInfo.name}
                            </span>
                          )}
                        </DocumentMeta>
                        {doc.synopsis && (
                          <div
                            style={{
                              fontSize: '0.75rem',
                              color: '#999',
                              marginTop: '0.25rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {doc.synopsis}
                          </div>
                        )}
                      </DocumentInfo>
                      {isSelected && <SelectedBadge>Selected</SelectedBadge>}
                    </DocumentItem>
                  );
                })}
              </DocumentList>
            )}

            <div
              style={{
                display: 'flex',
                justifyContent:
                  filteredBySearch.length === 0 ? 'flex-end' : 'space-between',
                alignItems: 'center',
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid ${COLORS.BORDER_LIGHT}',
              }}
            >
              {filteredBySearch.length > 0 && (
                <div
                  style={{ fontSize: '0.875rem', color: COLORS.TEXT_SECONDARY }}
                >
                  {localSelectedIds.length} document(s) selected
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button
                  variant="outline"
                  onClick={() => setShowUploadModal(true)}
                  style={
                    filteredBySearch.length > 0
                      ? { marginRight: 'auto' }
                      : undefined
                  }
                  data-tour="doc-selector-upload-documents-button"
                >
                  📤 Upload Documents
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  data-tour="doc-selector-cancel-button"
                >
                  Cancel
                </Button>
                {filteredBySearch.length > 0 && (
                  <Button
                    variant="primary"
                    onClick={handleConfirm}
                    data-tour="doc-selector-add-documents-button"
                  >
                    Add Documents
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {/* Upload Documents Modal */}
        <Modal
          isOpen={showUploadModal}
          onClose={resetUploadModal}
          title="Upload Documents"
          size="large"
          footer={
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.5rem',
              }}
            >
              <Button
                variant="outline"
                onClick={resetUploadModal}
                data-tour="doc-selector-upload-modal-cancel-button"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleUploadDocuments}
                isLoading={isUploading}
                disabled={pendingFiles.length === 0 || isUploading}
                data-tour="doc-selector-upload-files-button"
              >
                {isUploading ? 'Uploading...' : 'Upload Files'}
              </Button>
            </div>
          }
        >
          <div
            style={{ marginBottom: '1rem' }}
            data-tour="doc-selector-upload-file-input-area"
          >
            <Input
              type="file"
              label="Select Files"
              multiple
              onFileSelect={handleFileSelectWrapper}
              tooltip="Supports PDF, DOCX, TXT, and more • Auto-OCR"
              data-tour="doc-selector-upload-file-input"
            />
            {pendingFiles.length > 0 && (
              <div
                style={{
                  fontSize: '0.875rem',
                  color: COLORS.TEXT_SECONDARY,
                  marginTop: '0.5rem',
                }}
              >
                {pendingFiles.length} file(s) selected:
                {pendingFiles.map((file, index) => (
                  <div key={index}>• {file.name}</div>
                ))}
              </div>
            )}
          </div>
          <div
            style={{ marginTop: '1rem' }}
            data-tour="doc-selector-upload-modal"
          >
            <label
              data-tour="doc-selector-version-files-checkbox-label"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={versionIfExists}
                onChange={(e) => setVersionIfExists(e.target.checked)}
              />
              <span style={{ fontSize: '0.875rem' }}>
                Version files if they already exist
              </span>
            </label>
          </div>
        </Modal>
      </div>
    </Modal>
  );
};
